/**
 * Whose page a client is actually looking at.
 * @file src/web/shared/publicBrand.ts
 *
 * ── The problem this solves ───────────────────────────────────────────────
 * The public pages — the intake form, the signing page, the booking page —
 * are laid out by one shared layout that renders the header, while the thing
 * that KNOWS whose form it is renders inside it. So the header said "Secure
 * intake" beside our own mark, and a client who had just clicked a link on
 * their lawyer's website landed on a page with somebody else's branding on
 * it and no sign of the firm they were contacting.
 *
 * ── Why a store rather than context ───────────────────────────────────────
 * The layout is mounted by the router above the page, so the page cannot
 * wrap it in a provider. A module-level store with useSyncExternalStore is
 * the smallest thing that lets the child tell the parent, and it survives
 * the page being swapped for another under the same layout.
 *
 * @llm-rule WHEN: A public page knows which firm it belongs to
 * @llm-rule AVOID: Fetching the firm again in the layout — the page has it
 */
import { useSyncExternalStore } from 'react';

export interface PublicBrand {
  name: string | null;
  /** Path on the API, or null when the firm has not uploaded one. */
  logoUrl: string | null;
  /**
   * Has the page told us yet?
   *
   * Without this the header cannot tell "this firm has no logo" from "we
   * have not asked yet", so it rendered our own mark for the few hundred
   * milliseconds before the firm's arrived and then swapped — which a firm
   * watching their own form notices immediately, and reads as their brand
   * being an afterthought.
   */
  known: boolean;
}

const EMPTY: PublicBrand = { name: null, logoUrl: null, known: false };

let current: PublicBrand = EMPTY;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

/** Called by the page once it knows. Idempotent: same values, no re-render. */
export function setPublicBrand(next: Omit<PublicBrand, 'known'>) {
  if (current.known && next.name === current.name && next.logoUrl === current.logoUrl) return;
  current = { ...next, known: true };
  emit();
}

/** Called when a page unmounts, so the next one does not inherit a firm. */
export function clearPublicBrand() {
  setPublicBrand(EMPTY);
}

export function usePublicBrand(): PublicBrand {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => EMPTY,
  );
}
